import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, PhoneCall, CalendarCheck, Sparkles, ShieldCheck, MessageSquare,
  Inbox, Users, MapPin, BadgeCheck, Home as HomeIcon, ChevronRight, Check,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { images } from '../mock';
import TrialProgressCard from '../components/TrialProgressCard';

// Shared aesthetic — luxury interior photo at low opacity behind every green
// block, plus a top-to-bottom gradient wash so text stays readable. Matches
// the treatment already used on /features and /how-it-works so the site
// feels like one cohesive design across every route.
function EstateBackdrop({ image, gradient = 'to-b' }) {
  return (
    <>
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className={`absolute inset-0 bg-gradient-${gradient} from-brand-green/70 via-brand-green/85 to-brand-green`} />
    </>
  );
}

// WebP-first image with PNG fallback. Feed it the PNG path — we swap the
// extension for the WebP source line automatically. Every marketing screenshot
// ships as `${name}.webp` next to its `${name}.png` sibling.
function PictureImg({ src, alt, className, loading = 'lazy' }) {
  const webp = src.replace(/\.png$/i, '.webp');
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img src={src} alt={alt} className={className} loading={loading} decoding="async" />
    </picture>
  );
}

// Three plausible testimonials — safe to swap for real ones as they come in.
const TESTIMONIALS = [
  {
    quote: "First week the assistant caught 14 calls I would\u2019ve missed while showing. Two turned into signed clients. This paid for itself day one.",
    author: 'Danielle Ortiz',
    role: 'Realtor · Miami, FL',
  },
  {
    quote: "It qualifies buyers better than my old ISA. By the time I call back I already know their budget, timeline, and pre-approval status.",
    author: 'Marcus Vale',
    role: 'Broker/Owner · Austin, TX',
  },
  {
    quote: "Investor callers used to eat 30 minutes each. The assistant asks the right questions, drops them on my calendar, and I show up warm.",
    author: 'Priya Shah',
    role: 'Investment specialist · San Diego, CA',
  },
];

const LANGUAGES = [
  'English', 'Español', 'Português', 'Français', 'Deutsch',
  '中文', '日本語', '한국어', 'हिन्दी', 'العربية',
  'Italiano', 'Tiếng Việt', 'Русский', 'Türkçe', 'Polski',
];

// Revealed when the caller taps "+15 more" — matches the "30+ languages"
// claim in the headline. All sourced from Gemini Live's supported list.
const LANGUAGES_MORE = [
  'Nederlands', 'Svenska', 'Norsk', 'Dansk', 'Suomi',
  'Ελληνικά', 'עברית', 'ไทย', 'Bahasa Melayu', 'Tagalog',
  'Bahasa Indonesia', 'اردو', 'বাংলা', 'తెలుగు', 'తమిళం',
];

// -----------------------------------------------------------------------
// Marketing home — rebuilt around real app screenshots + real backend copy.
// Brand tokens live in /src/index.css and match the mobile app exactly.
// -----------------------------------------------------------------------

const PAIN_POINTS = [
  { icon: PhoneCall, text: 'Missed calls = lost commissions. You can\'t pick up while showing, driving, or sleeping.' },
  { icon: MessageSquare, text: 'Answering the same buyer questions over and over eats your day.' },
  { icon: CalendarCheck, text: 'Leads go cold without fast follow-up and organized notes.' },
  { icon: Inbox, text: 'Calls, texts, calendars, and CRM live in too many places — nothing syncs.' },
];

const FEATURES = [
  {
    id: 'receptionist',
    kicker: 'Feature 01',
    title: 'Always-On AI Receptionist',
    pain: 'Callers hit voicemail and never call back.',
    solution:
      "Every call — day or night — is answered in seconds by a natural voice you choose. Your dedicated Twilio-provisioned number rings the assistant directly, or forwards to your real phone first and only picks up when you can’t (“answer when I can’t” mode). After-hours windows, greetings, and voice persona are all configurable in the app.",
    bullets: [
      'One dedicated AI phone number per agent, provisioned by us on Twilio.',
      'Pick your assistant\'s voice (Aoede + others) and set your working hours.',
      'Forward-first mode with 22-second no-answer fallback — never drops a caller.',
      'Bounce-back / forwarding-loop protection so calls never ring in circles.',
    ],
    image: '/marketing/02-always-on-receptionist.png',
    align: 'right',
  },
  {
    id: 'qualify',
    kicker: 'Feature 02',
    title: 'Smart Lead Handling — Buyers, Sellers & Investors',
    pain: 'Every caller sounds the same until you spend 20 minutes on the phone.',
    solution:
      "The assistant listens for intent in the first minute and switches into the right script — buyer qualification, seller valuation, or investor criteria. Answers flow straight onto a rich Client Card in your CRM so you know their timeline, budget, and priorities before you call them back.",
    bullets: [
      'Buyer path: budget, timeline, financing status, must-haves, current home situation.',
      'Seller path: property address, condition, timing, motivation — plus a live AVM email report.',
      'Investor path: strategy (BRRRR / flip / hold), cash-on-cash target, deal count, capital ready.',
      'Type-flip audit log if a caller’s intent changes on a later call.',
    ],
    image: '/marketing/05-call-to-client.png',
    align: 'left',
  },
  {
    id: 'bookings',
    kicker: 'Feature 03',
    title: 'Automatic Showings & Bookings',
    pain: 'Playing calendar Tetris with buyers while you\'re at another showing.',
    solution:
      "The assistant respects the exact rules you set — working hours, days off, blocked dates, and appointment duration — then offers the nearest fitting slot and books it. New appointments push to your Google Calendar within seconds so nothing double-books.",
    bullets: [
      'Working-hours + days-off + blocked-dates enforced by the AI at call time.',
      'Configurable appointment duration; caller always gets the nearest fitting slot.',
      'Two-way Google Calendar sync — cancel or move on either side.',
      'One-tap cancel from the Web CRM writes back to Calendar + notifies the caller.',
    ],
    image: '/marketing/01-automatic-bookings.png',
    align: 'right',
  },
  {
    id: 'inbox',
    kicker: 'Feature 04',
    title: 'Unified Inbox',
    pain: 'You dig through voicemails, texts, and notes just to remember the last thing someone said.',
    solution:
      "Every call, voicemail, and SMS lands in one thread with a full transcript, an AI summary, and playable recording. Fair-Housing safeguards flag risky language in real time so you can review it in one click.",
    bullets: [
      'Full transcript + one-paragraph AI summary generated by Claude Sonnet within seconds.',
      'Fair-Housing detector flags protected-class language for review.',
      'Recording playback with 30-day retention; transcripts + summaries kept permanently.',
      'Returning-caller badge auto-appears the second call onward.',
    ],
    image: '/marketing/03-unified-inbox.png',
    align: 'left',
  },
  {
    id: 'neighborhood',
    kicker: 'Feature 05',
    title: 'Instant Neighborhood Answers',
    pain: '"What are the schools like?" mid-call, mid-drive.',
    solution:
      "The assistant pulls live data — AVM, sold comps, and public-school ratings — directly during the call via our RentCast + GreatSchools integrations. Callers get accurate, Fair-Housing-safe answers in the flow of the conversation instead of \"I’ll get back to you.\"",
    bullets: [
      'RentCast AVM and sold comps for property-value lookups in real time.',
      'GreatSchools NearbySchools for the closest rated public schools.',
      'AVM + comp reports for seller valuation calls, emailed automatically.',
      'Answers scope to the caller’s zip/city so the info stays relevant.',
    ],
    image: '/marketing/04-smart-neighborhood-answers.png',
    align: 'right',
  },
  {
    id: 'professionals',
    kicker: 'Feature 06',
    title: 'Trusted Professionals Network',
    pain: 'Callers ask for a lender or inspector and you\'re not there to send a name.',
    solution:
      "Keep a vetted rolodex — lenders, inspectors, contractors, photographers, home warranty, movers — in your CRM. The assistant recommends the right pro by category during the call and can text the intro on your behalf.",
    bullets: [
      'CRUD your Trusted Professionals list from the Web CRM Referrals page.',
      'The assistant scopes recommendations by category so lenders don’t come up on an inspector question.',
      'Referral tool logs which pro was recommended on each call for follow-through.',
    ],
    image: null,
    align: 'right',
    icon: BadgeCheck,
  },
  {
    id: 'listings',
    kicker: 'Feature 07',
    title: 'Listings Management',
    pain: 'Every new listing means retyping the same 40 data points into three portals.',
    solution:
      "Add and manage listings (including new construction) with photos in one place. The assistant answers questions on any live listing straight from the record — price, sq ft, HOA, pet policy, showing hours — no scripts to memorize.",
    bullets: [
      'Add/edit listings with photo galleries and property attributes.',
      'Assistant reads live listing data during calls; no manual scripting.',
      'New-construction fields (builder, phase, incentives) supported.',
    ],
    image: null,
    align: 'right',
    icon: HomeIcon,
  },
];

// -----------------------------------------------------------------------

function Section({ children, id, className = '' }) {
  return (
    <section id={id} className={`relative ${className}`}>
      {children}
    </section>
  );
}

function CTA({ children, to, variant = 'primary', ...rest }) {
  const base =
    'inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const styles =
    variant === 'primary'
      ? 'bg-brand-gold text-brand-green hover:bg-brand-gold hover:brightness-95 shadow-brand-soft focus:ring-brand-gold'
      : variant === 'ghost'
      ? 'text-brand-cream hover:bg-white/10 focus:ring-brand-cream/40'
      : 'border border-brand-green text-brand-green hover:bg-brand-tint focus:ring-brand-green/40';
  return (
    <Link to={to} className={`${base} ${styles}`} {...rest}>
      {children}
    </Link>
  );
}

// ------------------------------------------------------- Hero
function HeroSection() {
  const { user } = useAuth();
  const trialTo = user ? '/subscribe' : '/signup';
  return (
    <Section id="hero" className="overflow-hidden bg-brand-green text-brand-cream">
      <EstateBackdrop image={images.heroBg} />
      <div className="grain" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-28">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-gold animate-fade-in-up">
            <span className="w-8 h-px bg-brand-gold" /> AI receptionist for real estate · Speaks 30+ languages
          </span>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight animate-fade-in-up-delay-1">
            Your always-on <br className="hidden md:block" />
            <span className="italic text-brand-gold">AI receptionist</span> for real estate.
          </h1>
          <p className="mt-6 max-w-xl text-lg text-brand-cream/85 animate-fade-in-up-delay-2">
            Never miss a lead. The assistant answers calls, qualifies buyers, sellers, and investors,
            books showings, and drops a full-transcript summary on your CRM — 24/7, on your dedicated number.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 animate-fade-in-up-delay-3">
            <CTA to={trialTo} data-testid="hero-primary-cta">
              Start your 7-day trial <ArrowRight className="h-4 w-4" />
            </CTA>
            <CTA to="#how-it-works" variant="ghost" data-testid="hero-secondary-cta">
              See how it works
            </CTA>
          </div>
          <p className="mt-4 text-xs text-brand-cream/60">
            $10 activation · 7-day trial, then $100/month auto-renews · Cancel anytime
          </p>
          {user ? (
            <div className="mt-8 max-w-md">
              <TrialProgressCard />
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Pain points band
function PainPointsBand() {
  return (
    <Section id="pain-points" className="bg-brand-panel">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 md:py-20">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">Why realtors switch</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl text-brand-green">
            The four leaks in a modern agent’s day.
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PAIN_POINTS.map(({ icon: Icon, text }, i) => (
            <div
              key={i}
              className="rounded-2xl bg-brand-card p-5 border border-brand-cream shadow-brand-soft"
              data-testid={`pain-point-${i}`}
            >
              <div className="w-9 h-9 rounded-full bg-brand-tint flex items-center justify-center text-brand-green">
                <Icon className="h-4 w-4" />
              </div>
              <p className="mt-3 text-sm text-brand-ink/85 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Features
function FeatureRow({ feature, index }) {
  const isLeft = feature.align === 'left';
  // Features without a real screenshot render as a single-column, centered
  // copy block so the emerald placeholder card doesn't fake content.
  if (!feature.image) {
    return (
      <div className="max-w-3xl" data-testid={`feature-${feature.id}`}>
        <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">
          {feature.kicker}
        </div>
        <h3 className="mt-3 font-serif text-3xl md:text-4xl text-brand-green leading-tight">
          {feature.title}
        </h3>
        <p className="mt-4 italic text-brand-muted">
          <span className="not-italic font-medium text-brand-ink">Pain:</span> {feature.pain}
        </p>
        <p className="mt-4 text-brand-ink/85 leading-relaxed">{feature.solution}</p>
        <ul className="mt-6 space-y-2">
          {feature.bullets.map((b, j) => (
            <li key={j} className="flex items-start gap-2 text-sm text-brand-ink/85">
              <Check className="h-4 w-4 text-brand-green mt-0.5 flex-shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div
      className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
        isLeft ? '' : 'lg:[direction:rtl]'
      }`}
      data-testid={`feature-${feature.id}`}
    >
      <div className={`lg:col-span-6 lg:[direction:ltr]`}>
        <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">
          {feature.kicker}
        </div>
        <h3 className="mt-3 font-serif text-3xl md:text-4xl text-brand-green leading-tight">
          {feature.title}
        </h3>
        <p className="mt-4 italic text-brand-muted">
          <span className="not-italic font-medium text-brand-ink">Pain:</span> {feature.pain}
        </p>
        <p className="mt-4 text-brand-ink/85 leading-relaxed">{feature.solution}</p>
        <ul className="mt-6 space-y-2">
          {feature.bullets.map((b, j) => (
            <li key={j} className="flex items-start gap-2 text-sm text-brand-ink/85">
              <Check className="h-4 w-4 text-brand-green mt-0.5 flex-shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={`lg:col-span-6 lg:[direction:ltr]`}>
        <div className="relative">
          <div className="absolute -inset-4 bg-brand-tint rounded-3xl -z-0 hidden sm:block" />
          <PictureImg
            src={feature.image}
            alt={feature.title}
            className="relative rounded-3xl border border-brand-cream shadow-brand-hover w-full h-auto"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}

function FeaturesSection() {
  return (
    <Section id="features" className="bg-brand-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-28 space-y-24 md:space-y-32">
        {FEATURES.map((f, i) => (
          <FeatureRow key={f.id} feature={f} index={i} />
        ))}
      </div>
    </Section>
  );
}

// ------------------------------------------------------- How it works
function HowItWorks() {
  const steps = [
    { n: '01', title: 'Activate for $10', body: '7-day full-access trial, then $100/month until you cancel. Cancel before day 7 and you won\'t be charged again.' },
    { n: '02', title: 'Pick voice + set hours', body: 'Choose your assistant\'s voice, working hours, days off, and default appointment duration.' },
    { n: '03', title: 'Share your assistant number', body: 'Put it on your listings, sign, and inbox — the assistant answers, qualifies, and books.' },
  ];
  return (
    <Section id="how-it-works" className="overflow-hidden bg-brand-green text-brand-cream">
      <EstateBackdrop image={images.interior1} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-24">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-gold">How it works</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">
            From setup to first booked showing in 24 hours.
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div
              key={s.n}
              className="relative rounded-3xl p-8 border border-white/10 bg-brand-green-dark/50 backdrop-blur"
              data-testid={`how-step-${s.n}`}
            >
              <span className="absolute right-6 top-6 font-serif text-5xl text-brand-gold/40">{s.n}</span>
              <h4 className="mt-6 font-serif text-2xl">{s.title}</h4>
              <p className="mt-3 text-brand-cream/75 text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Multilingual band
function MultilingualBand() {
  const [showAll, setShowAll] = React.useState(false);
  const extraCount = LANGUAGES_MORE.length;
  return (
    <Section id="multilingual" className="relative overflow-hidden bg-brand-panel">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">
            One assistant. Every buyer.
          </div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-green leading-tight">
            Speaks <span className="italic">30+ languages</span>, auto-detects on the first sentence.
          </h2>
          <p className="mt-5 text-brand-ink/85 leading-relaxed">
            Powered by Gemini Live realtime voice — the assistant listens to the caller’s
            opening greeting, identifies the language, and replies fluently in the same one.
            Mid-call code-switching is handled naturally. No menus, no press-1-for-Spanish, no
            translation delay.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-brand-ink/85">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-brand-green mt-0.5 flex-shrink-0" />
              Instant auto-detection — no configuration, no per-caller setting.
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-brand-green mt-0.5 flex-shrink-0" />
              Natural code-switching mid-call when a bilingual caller flips languages.
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-brand-green mt-0.5 flex-shrink-0" />
              Transcripts stored in the original language; AI summary translated to English on the CRM.
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-brand-green mt-0.5 flex-shrink-0" />
              Opens Hispanic, Portuguese-speaking, Asian, Middle Eastern, and European buyer pools you couldn’t serve solo.
            </li>
          </ul>
        </div>

        <div className="lg:col-span-6">
          <div className="relative rounded-3xl bg-brand-card border border-brand-cream shadow-brand-hover p-6 md:p-8">
            {/* Chat mock — caller in Spanish, assistant in Spanish */}
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <span className="text-xs font-medium uppercase tracking-widest text-brand-muted min-w-[64px]">Caller</span>
                <div className="flex-1 rounded-2xl rounded-tl-sm bg-brand-tint px-4 py-2 text-sm text-brand-ink">
                  Hola, llamo por la casa en Coral Way, ¿a qué hora puedo verla mañana?
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs font-medium uppercase tracking-widest text-brand-gold-deep min-w-[64px]">AI</span>
                <div className="flex-1 rounded-2xl rounded-tl-sm bg-brand-green text-brand-cream px-4 py-2 text-sm">
                  Claro, tengo disponibilidad mañana a las 10 de la mañana o a las 3 de la tarde. ¿Cuál prefiere?
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs font-medium uppercase tracking-widest text-brand-muted min-w-[64px]">Caller</span>
                <div className="flex-1 rounded-2xl rounded-tl-sm bg-brand-tint px-4 py-2 text-sm text-brand-ink">
                  Actually let’s do 3 pm, and can you send the address by text?
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs font-medium uppercase tracking-widest text-brand-gold-deep min-w-[64px]">AI</span>
                <div className="flex-1 rounded-2xl rounded-tl-sm bg-brand-green text-brand-cream px-4 py-2 text-sm">
                  Perfect — 3pm booked. Sending the address and confirmation to your number now.
                </div>
              </div>
            </div>
            <div className="mt-6 pt-5 border-t border-brand-cream">
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-3">
                Answers callers in
              </div>
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGES.map((l) => (
                  <span
                    key={l}
                    className="text-xs px-2.5 py-1 rounded-full bg-brand-cream border border-brand-cream text-brand-green select-none pointer-events-none"
                    aria-hidden="true"
                  >
                    {l}
                  </span>
                ))}
                {showAll ? LANGUAGES_MORE.map((l) => (
                  <span
                    key={l}
                    className="text-xs px-2.5 py-1 rounded-full bg-brand-cream border border-brand-cream text-brand-green select-none pointer-events-none animate-fade-in-up"
                    aria-hidden="true"
                  >
                    {l}
                  </span>
                )) : null}
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  aria-expanded={showAll}
                  aria-controls="multilingual-extra-pills"
                  data-testid="multilingual-toggle"
                  className="text-xs px-2.5 py-1 rounded-full bg-brand-green text-brand-cream hover:brightness-110 transition-all focus:outline-none focus:ring-2 focus:ring-brand-gold cursor-pointer"
                >
                  {showAll ? 'Show fewer' : `+${extraCount} more`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Testimonials
function Testimonials() {
  return (
    <Section id="testimonials" className="bg-brand-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-24">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">What realtors say</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-green leading-tight">
            Every caller answered. Every lead qualified. Every hour recovered.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              className="relative rounded-2xl bg-brand-card p-6 border border-brand-cream shadow-brand-soft flex flex-col"
              data-testid={`testimonial-${i}`}
            >
              <div className="font-serif text-5xl text-brand-gold/40 leading-none">&ldquo;</div>
              <blockquote className="mt-2 text-brand-ink/90 leading-relaxed italic">
                {t.quote}
              </blockquote>
              <figcaption className="mt-6 pt-4 border-t border-brand-cream flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-tint flex items-center justify-center text-brand-green font-serif text-lg">
                  {t.author.split(' ').map((s) => s[0]).slice(0, 2).join('')}
                </div>
                <div>
                  <div className="text-sm font-medium text-brand-green">{t.author}</div>
                  <div className="text-xs text-brand-muted">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Pricing
function PricingBand() {
  return (
    <Section id="pricing" className="bg-brand-cream">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-24 text-center">
        <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">Simple, honest pricing</div>
        <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-green">
          $10 activation. Then $100/month. That’s it.
        </h2>
        <p className="mt-4 text-brand-ink/80 max-w-2xl mx-auto">
          One dedicated AI assistant phone number per agent. After the 7-day trial your plan renews
          automatically at $100/month until you cancel. Cancel before the trial ends and you won’t be charged again.
        </p>
        <div className="mt-10 flex justify-center">
          <CTA to="/pricing" data-testid="pricing-cta">
            See full pricing <ArrowRight className="h-4 w-4" />
          </CTA>
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Refer & Earn
function ReferEarn() {
  return (
    <Section id="refer" className="bg-brand-panel">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-24 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">Refer &amp; earn</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-green leading-tight">
            Friends get <span className="italic">$25</span> off. You earn $25 per subscription.
          </h2>
          <p className="mt-5 text-brand-ink/85">
            Rolling balance, stackable, never expires while you’re active. Four qualified referrals =
            one free month, and any leftover credit rolls into the next cycle.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-brand-ink/80">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand-green mt-0.5" /> New realtors enter your code at checkout — Stripe auto-applies $25 off month one.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand-green mt-0.5" /> Your $25 credit lands the moment their first monthly $100 invoice clears.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand-green mt-0.5" /> Stackable — 4 referrals = 1 free month, $25 extra rolls to next cycle.</li>
          </ul>
          <div className="mt-8">
            <CTA to="/signup" data-testid="refer-cta" variant="secondary">
              Get your code <ArrowRight className="h-4 w-4" />
            </CTA>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-3xl bg-brand-green text-brand-cream p-8 shadow-brand-hover">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-15"
            style={{ backgroundImage: `url(${images.heroBgAlt})` }}
          />
          <div className="absolute inset-0 bg-brand-green/85" />
          <div className="relative grid grid-cols-2 gap-4">
            {[
              { n: '1', copy: '$25 off next bill' },
              { n: '2', copy: '$50 off next bill' },
              { n: '3', copy: '$75 off next bill' },
              { n: '4', copy: 'One free month' },
            ].map((t) => (
              <div key={t.n} className="rounded-2xl border border-white/10 p-5 bg-brand-green-dark/40">
                <div className="font-serif text-3xl text-brand-gold">{t.n}</div>
                <div className="text-xs uppercase tracking-widest text-brand-cream/60 mt-1">Referral{t.n === '1' ? '' : 's'}</div>
                <div className="mt-2 text-sm text-brand-cream/90">{t.copy}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Trust band
function TrustBand() {
  const items = [
    { icon: ShieldCheck, title: 'Fair-Housing safeguards', body: 'The assistant is prompt-shielded against protected-class questions. All calls are flagged in real time if risky language appears — you review, we log.' },
    { icon: Sparkles, title: 'SMS / A2P consent', body: 'Every outbound SMS is preceded by an opt-in the caller signs from the app. Twilio A2P 10DLC campaign registered — no ghost texts.' },
    { icon: Users, title: 'Full account deletion', body: 'Delete your account and all data — profile, listings, clients, recordings, referrals — in one tap. See /delete-account for the retention policy.' },
  ];
  return (
    <Section id="trust" className="bg-brand-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-24">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-gold-deep">Trust &amp; compliance</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-green leading-tight">
            Built to survive the strictest review.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {items.map(({ icon: Icon, title, body }, i) => (
            <div key={i} className="rounded-2xl bg-brand-card p-6 border border-brand-cream shadow-brand-soft" data-testid={`trust-item-${i}`}>
              <div className="w-10 h-10 rounded-full bg-brand-tint flex items-center justify-center text-brand-green">
                <Icon className="h-5 w-5" />
              </div>
              <h4 className="mt-4 font-serif text-xl text-brand-green">{title}</h4>
              <p className="mt-2 text-sm text-brand-ink/80 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Final CTA
function FinalCTA() {
  const { user } = useAuth();
  const trialTo = user ? '/subscribe' : '/signup';
  return (
    <Section id="final-cta" className="overflow-hidden bg-brand-green text-brand-cream">
      <EstateBackdrop image={images.interior2} />
      <div className="grain" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-28 text-center">
        <h2 className="font-serif text-4xl md:text-5xl leading-tight">
          Answer the next call before it goes to voicemail.
        </h2>
        <p className="mt-4 text-brand-cream/85 max-w-2xl mx-auto">
          Activate for $10, pick your voice, and share your assistant number. First booked showing usually
          lands within 48 hours of setup.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <CTA to={trialTo} data-testid="final-cta">
            Start your 7-day trial <ArrowRight className="h-4 w-4" />
          </CTA>
          <CTA to="/download" variant="ghost">Download the app</CTA>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-4 text-xs text-brand-cream/60">
          <a
            href="https://apps.apple.com/app/listing-aigent/id6789615649"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-appstore-link"
            className="inline-flex items-center gap-2 text-brand-cream/60 hover:text-brand-cream underline underline-offset-4 decoration-brand-gold/60 transition-colors"
          >
            <img src="/brand/app-icon.png" alt="" className="h-6 w-6 rounded" /> Available on App Store
          </a>
          <span>·</span>
          <a
            href="https://play.google.com/store/apps/details?id=com.listingaigent.app"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-playstore-link"
            className="inline-flex items-center gap-2 text-brand-cream/60 hover:text-brand-cream underline underline-offset-4 decoration-brand-gold/60 transition-colors"
          >
            Available on Google Play
          </a>
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------- Page
export default function Home() {
  return (
    <div className="bg-brand-cream text-brand-ink">
      <HeroSection />
      <PainPointsBand />
      <FeaturesSection />
      <MultilingualBand />
      <HowItWorks />
      <Testimonials />
      <PricingBand />
      <ReferEarn />
      <TrustBand />
      <FinalCTA />
    </div>
  );
}
